/* eslint complexity: ["error", {"max": 22}] */
import { differenceInMinutes, setMilliseconds } from 'date-fns';

import { getFormattedDateAndTime } from '../../../core/lib/entry.browser.js';
import type { DateTime } from '../../../schema/frameworks/entry.js';
import { type EventStatus, type SportType } from '../../../schema/sport/entry.js';
import { config as Default } from './configs/default.js';
import { config as Football } from './configs/football.js';
import { config as NonTimebased } from './configs/nonTimeBased.js';
import { getSportInfoMatchState, hasMatchStartSurpassed } from './utils.js';

type GetStatusTextParams = {
  sportEventStatus: EventStatus;
  sportType: SportType;
  statusTime: DateTime;
  statusTextLength?: 'full' | 'short';
  matchStart?: DateTime;
};

const sportTypeToConfig: Partial<Record<SportType, typeof NonTimebased | typeof Football>> = {
  Soccer: Football,
  Tennis: NonTimebased,
  Golf: NonTimebased,
  Speedway: NonTimebased,
  SkiingAlpine: NonTimebased,
  Formula1: NonTimebased,
  Athletics: NonTimebased,
  SkiingCrossCountry: NonTimebased,
  WinterOlympic: NonTimebased,
  Biathlon: NonTimebased,
  MartialArt: NonTimebased,
  Trotting: NonTimebased,
  Specialties: NonTimebased,
  SummerOlympic: NonTimebased,
};

/**
 * Returns a statusstring with a short statusText or live matchminutes.
 *
 * @param root0 - parameters object
 * @param root0.sportEventStatus - match sportEventStatus (See SportEventStatus in components/sport/sport-event-common/assets/javascripts/sport-event-status.es6)
 * @param root0.sportType - type for sport
 * @param root0.statusTime - date compatible string with latest time a status was sent from server
 * @param root0.statusTextLength - length of statusText (short or long)
 * @param root0.matchStart - matchStart time from api
 * @returns - statustext string.
 */
export function getStatusText({
  sportEventStatus,
  sportType,
  statusTime,
  statusTextLength = 'full',
  matchStart,
}: GetStatusTextParams): { matchStatusText: string; formattedTime?: string; formattedDate?: string } {
  const defaultReturn = Default.statusTexts[sportEventStatus][statusTextLength];
  const matchState = getSportInfoMatchState(sportEventStatus);
  if (matchStart && matchState === 'NotStarted') {
    // If surpassing matchStart and still has "not started" status
    const hasSurpassed = hasMatchStartSurpassed(sportType, matchStart, sportEventStatus);
    if (hasSurpassed) {
      return {
        matchStatusText: NonTimebased.statusTexts['Ended']?.[statusTextLength] ?? defaultReturn,
      };
    }
    if (sportEventStatus === 'Delayed') {
      return {
        matchStatusText: Default.statusTexts['Delayed'][statusTextLength],
      };
    }

    return {
      matchStatusText: getFormattedDateAndTime(matchStart, {
        shouldCapitalizeFirstLetter: true,
        shouldUseNameOfDay: true,
        timeFormatOptions: 'hourMinute',
        formatOptions: 'dateAndTime',
        dateFormatOptions: statusTextLength === 'short' ? 'shortDate' : 'dayAsText',
      }),
      formattedTime: getFormattedDateAndTime(matchStart, {
        timeFormatOptions: 'hourMinute',
        formatOptions: 'timeOnly',
      }),
      formattedDate: getFormattedDateAndTime(matchStart, {
        shouldCapitalizeFirstLetter: true,
        shouldUseNameOfDay: true,
        dateFormatOptions: statusTextLength === 'short' ? 'shortDate' : 'dayAsText',
        formatOptions: 'dateOnly',
      }),
    };
  }
  const config: typeof NonTimebased | typeof Football | undefined = sportTypeToConfig[sportType];

  // Return default config if the sport doesn't have one defined
  if (!config) {
    return {
      matchStatusText: defaultReturn,
    };
  }

  // if live
  if (statusTime && matchState === 'Ongoing') {
    if ('matchMinutesOverrides' in config && config.matchMinutesOverrides.includes(sportEventStatus)) {
      return {
        matchStatusText: config.statusTexts[sportEventStatus]?.[statusTextLength] ?? defaultReturn,
      };
    }

    const timeOffset = 'timeOffsets' in config && config.timeOffsets[sportEventStatus];

    if (timeOffset) {
      const statusDate = setMilliseconds(new Date(statusTime), 0);
      const now = setMilliseconds(new Date(), 0);
      let statusTimeDiff = differenceInMinutes(now, statusDate, { roundingMethod: 'ceil' });
      statusTimeDiff += timeOffset.start + 1;

      if (statusTimeDiff > timeOffset.end) {
        return {
          matchStatusText: `${timeOffset.end}+'`,
        };
      }

      return {
        matchStatusText: `${statusTimeDiff}'`,
      };
    }
  }

  const statusText = config.statusTexts[sportEventStatus] && config.statusTexts[sportEventStatus][statusTextLength];
  if (statusText) {
    return {
      matchStatusText: statusText,
    };
  }

  // Return default statusText if none is defined in the current sport config
  return {
    matchStatusText: defaultReturn,
  };
}
